import {
  Checkbox,
  Col,
  Form,
  Input,
  message,
  Row,
  Select,
  Tabs,
  TimePicker,
} from "antd";
import { AppButton } from "components/antd-ui";
import { Rule } from "antd/lib/form";
import { useForm } from "antd/lib/form/Form";
import { shopApi } from "api/shop.api";
import { MultiImageUpload } from "components/Upload/MultiImageUpload";
import { SingleImageUpload } from "components/Upload/SingleImageUpload";
import React, { useEffect, useMemo, useState } from "react";
import { Shop, ShopType, ShopTypeTrans } from "types/shop";
import { ModalStatus } from "types/modal";
import { UploadFile } from "antd/lib/upload/interface";
import { businessTypeStore } from "store/businessTypeStore";
import { observer } from "mobx-react";
import { areaStore } from "store/areaStore";
import { ShopChooseAddressModal } from "./components/ShopChooseAddressModal";
import { CoordAddress } from "components/Map/GoogleMapAutoComplete";
import TextArea from "antd/lib/input/TextArea";
import { authApi } from "api/auth.api";
import { getImageSize } from "utils";
import { PaymentMethod, PaymentMethodTrans } from "types/order";
import { PartnerTab } from "./components/TabPane/PartnerTab";
import dayjs from "dayjs";

const { Option } = Select;

interface ShopPaymentMethod {
  type: string;
  enable: boolean;
}

const paymentInit: ShopPaymentMethod[] = [
  {
    type: PaymentMethod.COD,
    enable: true,
  },
  {
    type: PaymentMethod.BankTransfer,
    enable: false,
  },
];

const style = {};

interface ShopForm extends Shop {
  businessTypeId: number;
  areaId: number;
  shopPaymentMethods: ShopPaymentMethod[];
}

export const ShopPage = observer(({ title = "" }) => {
  const rules: Rule[] = [{ required: true }];
  const [form] = useForm<ShopForm>();
  const [loading, setLoading] = useState(false);
  const [shop, setShop] = useState<Partial<Shop>>({});
  const [thumbnails, setThumbnails] = useState<string[]>([]);
  const [visibleChooseAddressModal, setVisibleChooseAddressModal] =
    useState(false);

  const [addressCoord, setAddressCoord] = useState<Partial<CoordAddress>>({});
  const [imageSize, setImageSize] = useState<any>({});

  useEffect(() => {
    fetchShop();
    areaStore.fetchList();
    businessTypeStore.fetchList();
  }, []);

  const fetchShop = async () => {
    const res = await authApi.profile();
    setShop(res.data);
  };

  useEffect(() => {
    form.resetFields();
    form.setFieldsValue({
      shopPaymentMethods: paymentInit,
    });

    if (shop) {
      const shopPaymentMethods: ShopPaymentMethod[] = shop.paymentMethodJson
        ? JSON.parse(shop.paymentMethodJson!)
        : paymentInit;
      form.setFieldsValue({
        ...shop,
        businessTypeId: shop.businessType?.id,
        areaId: shop.area?.id,
        shopPaymentMethods,
        avatar: shop?.customer?.avatar,
      });
      setAddressCoord({
        lat: shop.lat,
        address: shop.address,
        lng: shop.long,
      });
      getImageSize(setImageSize, form.getFieldValue("avatar"));
    }
  }, [shop]);

  const handleSubmitForm = async () => {
    await form.validateFields();
    try {
      setLoading(true);
      const open = form.getFieldValue("open");
      const close = form.getFieldValue("close");
      const shopPaymentMethods: ShopPaymentMethod[] =
        form.getFieldValue("shopPaymentMethods");
      if (shopPaymentMethods.every((e) => !e.enable)) {
        return message.error("Phương thức thanh toán tối thiểu phải chọn 1");
      }
      const dataPost = {
        shop: {
          ...form.getFieldsValue(),
          ...addressCoord,
          long: addressCoord.lng,
          open: dayjs(open).format("HH:mm"),
          close: dayjs(close).format("HH:mm"),
          // paymentMethodJson: JSON.stringify(shopPaymentMethods),
        },
        thumbnails,
        businessTypeId: form.getFieldValue("businessTypeId"),
        areaId: form.getFieldValue("areaId"),
        customerAvatar: form.getFieldValue("avatar"),
        potentialCustomer: form.getFieldValue("potentialCustomer"),
        connectionNeed: form.getFieldValue("connectionNeed"),
      };

      await authApi.updateProfile(dataPost);

      message.success("Cập nhật thành công");
    } finally {
      setLoading(false);
    }
  };

  const imageList = useMemo<UploadFile<any>[]>(() => {
    if (shop.thumbnails) {
      setThumbnails(shop.thumbnails.map((e) => e.url));
      return shop.thumbnails?.map((e) => ({
        name: e.id + "",
        url: e.url,
        uid: e.id + "",
      }));
    }
    setThumbnails([]);
    return [];
  }, [shop.thumbnails]);

  return (
    <>
      <Tabs defaultActiveKey="1" type="line">
        <Tabs.TabPane tab="Thông tin" key="1">
          <Form layout="vertical" onFinish={handleSubmitForm} form={form}>
            <div className="app-page-form">
              <div className="app-page-form__row">
                <section className="app-page-form__section">
                  <h3 className="app-page-form__section-header">
                    Thông tin doanh nghiệp
                  </h3>
                  <Row gutter={16}>
                    <Col span={24}>
                      <Form.Item
                        label="Tên doanh nghiệp"
                        name="name"
                        rules={rules}
                      >
                        <Input placeholder="" />
                      </Form.Item>
                    </Col>

                    <Col span={24}>
                      <Form.Item label="Địa chỉ" rules={rules}>
                        <Form.Item noStyle name="address">
                          <Input readOnly placeholder="" />
                        </Form.Item>
                        <AppButton
                          type="link"
                          style={{ position: "absolute", right: 0, top: -32 }}
                          onClick={() => setVisibleChooseAddressModal(true)}
                        >
                          Chọn địa chỉ
                        </AppButton>
                      </Form.Item>
                    </Col>

                    <Col span={24}>
                      <Form.Item
                        label="Địa chỉ cụ thể (Số nhà, tòa nhà, tầng, lầu)"
                        name="address1"
                      >
                        <Input placeholder="" />
                      </Form.Item>
                    </Col>

                    <Col span={12}>
                      <Form.Item
                        label="Số điện thoại"
                        name="phone"
                        rules={rules}
                      >
                        <Input placeholder="" />
                      </Form.Item>
                    </Col>

                    <Col span={12}>
                      <Form.Item label="Email" name="email" rules={rules}>
                        <Input placeholder="" />
                      </Form.Item>
                    </Col>

                    <Col span={12}>
                      <Form.Item shouldUpdate noStyle>
                        {() =>
                          form.getFieldValue("type") == ShopType.Company && (
                            <Form.Item label="Website" name="website">
                              <Input placeholder="" />
                            </Form.Item>
                          )
                        }
                      </Form.Item>
                    </Col>

                    <Col span={24}>
                      <Form.Item
                        label="Giới thiệu về doanh nghiệp"
                        name="description"
                      >
                        <TextArea rows={5} />
                      </Form.Item>
                    </Col>

                    <Col span={12}>
                      <Form.Item
                        label="Loại hình kinh doanh"
                        name="businessTypeId"
                        rules={rules}
                      >
                        <Select style={{ width: "100%" }}>
                          {businessTypeStore.list.map((e) => (
                            <Option key={e.id} value={e.id}>
                              {e.name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Lĩnh vực kinh doanh"
                        name="businessArea"
                        rules={rules}
                      >
                        <Input />
                      </Form.Item>
                    </Col>

                    <Col span={24}>
                      <Form.Item shouldUpdate noStyle>
                        {() => (
                          <>
                            {form.getFieldValue("type") == ShopType.Retail && (
                              <Form.Item
                                name="shipContent"
                                label="Ghi chú giao hàng"
                              >
                                <TextArea rows={3} />
                              </Form.Item>
                            )}
                          </>
                        )}
                      </Form.Item>
                    </Col>
                  </Row>
                </section>

                <section className="app-page-form__section">
                  <h3 className="app-page-form__section-header">
                    Hình ảnh & Thông tin thêm
                  </h3>

                  <Form.Item shouldUpdate={true}>
                    {() => (
                      <Form.Item label="Ảnh đại diện" name="avatar">
                        <SingleImageUpload
                          onUploadOk={(path: string) => {
                            form.setFieldsValue({ avatar: path });
                            getImageSize(setImageSize, path);
                          }}
                          imageUrl={form.getFieldValue("avatar")}
                        />
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                          }}
                        >
                          Kích thước hiện tại {imageSize.width} x{" "}
                          {imageSize.height}
                        </div>
                      </Form.Item>
                    )}
                  </Form.Item>

                  <Form.Item shouldUpdate={true}>
                    {() => (
                      <Form.Item label="Ảnh khác" name="thumbnails">
                        <MultiImageUpload
                          onUploadOk={(fileList) => setThumbnails(fileList)}
                          fileListProp={imageList}
                        />
                      </Form.Item>
                    )}
                  </Form.Item>

                  <Form.Item label="Top sản phẩm" name="potentialCustomer">
                    <TextArea rows={3} />
                  </Form.Item>

                  <Form.Item
                    label="Đối tác referral lý tưởng"
                    name="connectionNeed"
                  >
                    <TextArea rows={3} />
                  </Form.Item>
                </section>
              </div>

              <div className="app-page-form__footer">
                <AppButton tone="primary" htmlType="submit" loading={loading}>
                  Cập nhật
                </AppButton>
              </div>
            </div>

            <ShopChooseAddressModal
              visible={visibleChooseAddressModal}
              onClose={() => {
                setVisibleChooseAddressModal(false);
              }}
              onSubmitOk={(coord) => {
                setAddressCoord(coord);
                form.setFieldsValue({
                  address: coord.address,
                });
              }}
            />
          </Form>
        </Tabs.TabPane>
        <Tabs.TabPane tab="Đối tác/Khách hàng" key="2">
          <PartnerTab />
        </Tabs.TabPane>
      </Tabs>
    </>
  );
});
